import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import { Hash, Sparkles, Brain, Pencil, Loader2, X, Share2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import AstrologySection from "./AstrologySection";
import HumanDesignChart from "./HumanDesignChart";
import ShareCard from "./ShareCard";
import { DegreeReading, DegreeReadingError } from "./DegreeReading";
import { SIGN_GLYPH, stelliumsOf } from "../lib/astro";
import { ageFromBirthDate, saturnReturns, saturnChapter, PERSONAL_YEAR } from "../lib/growth";

const MBTI_TYPES = [];
for (const a of "EI") for (const b of "SN") for (const c of "TF") for (const d of "JP") MBTI_TYPES.push(a + b + c + d);

const TABS = [
  { k: "overview", l: "Overview" },
  { k: "astrology", l: "Astrology" },
  { k: "human-design", l: "Human Design" },
  { k: "psychology", l: "Psychology" },
  { k: "numerology", l: "Numerology" },
  { k: "growth", l: "Growth" },
];

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");

// ---- Shared building blocks ----
function SectionHeader({ overline, title, sub }) {
  return (
    <div className="mb-6">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-accent/90">{overline}</p>
      <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-1.5">{title}</h2>
      {sub && <p className="text-sm text-muted-foreground mt-2 max-w-xl">{sub}</p>}
    </div>
  );
}

function InsightCallout({ text, testid }) {
  if (!text) return null;
  return (
    <div data-testid={testid}
      className="rounded-2xl border-l-2 border-l-accent border border-white/10 bg-[#0A0A0F]/60 backdrop-blur-2xl p-5 mt-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent/90 mb-2 flex items-center gap-1.5">
        <Sparkles size={12} /> How this weaves together
      </p>
      <p className="text-sm leading-relaxed text-foreground/90">{text}</p>
    </div>
  );
}

function StatTile({ label, value, glyph, onClick, testid }) {
  const Comp = onClick ? motion.button : "div";
  return (
    <Comp
      {...(onClick ? { onClick, whileHover: { scale: 1.03 }, whileTap: { scale: 0.97 } } : {})}
      data-testid={testid}
      className={`text-left rounded-2xl border border-white/5 bg-white/[0.03] px-4 py-3 min-w-[110px] ${onClick ? "cursor-pointer hover:border-primary/40" : ""} transition-colors`}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        {glyph && <span className="font-serif text-primary text-lg leading-none">{glyph}</span>}
        <span className="font-serif text-xl text-foreground leading-tight">{value ?? "—"}</span>
      </div>
    </Comp>
  );
}

function Row({ label, value, onClick, testid }) {
  return (
    <div onClick={onClick} data-testid={testid}
      className={`flex items-center justify-between py-2 border-b border-white/5 last:border-0 ${onClick ? "cursor-pointer hover:text-primary group" : ""}`}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground group-hover:text-primary flex items-center gap-1.5">
        {value ?? "—"}{onClick && value && <Sparkles size={11} className="opacity-0 group-hover:opacity-100 text-primary" />}
      </span>
    </div>
  );
}

const tabFade = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.35, ease: "easeOut" },
};

export default function BlueprintView({ data, synthesis, profileId, onUpdate, onRegenerate }) {
  const n = data.natal || {};
  const hd = data.human_design || {};
  const num = data.numerology || {};
  const chin = data.chinese || {};
  const psy = data.psychology || null;

  const [tab, setTab] = useState("overview");
  const [insight, setInsight] = useState(null);
  const [editingMbti, setEditingMbti] = useState(false);
  const [sharing, setSharing] = useState(false);
  const interactive = !!profileId;
  const shareCardRef = useRef(null);
  const insightRef = useRef(null);
  const insightLabel = insight?.label;
  useEffect(() => {
    if (insightLabel) insightRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [insightLabel]);

  const pick = (frag) => (synthesis || []).find((x) => norm(x.tag).includes(norm(frag)));

  const explainSystem = async (system, detail, label) => {
    setInsight({ label, loading: true });
    try {
      const { data: r } = await api.post(`/profiles/${profileId}/explain-system`, { system, detail });
      setInsight({ label, text: r.text });
    } catch { setInsight(null); toast.error("Could not fetch insight"); }
  };

  const classicPlacement = async (planet, sign, house, degree) => {
    const label = `${planet} in ${sign}`;
    setInsight({ label, loading: true });
    try {
      const { data: r } = await api.post(`/profiles/${profileId}/explain-placement`, { planet, sign, house, degree });
      setInsight({ label, text: r.text });
    } catch { setInsight(null); toast.error("Could not fetch insight"); }
  };

  const explainPlacement = async (planet, sign, house, degree, includeQuestions = true) => {
    if (!interactive || !sign) return;
    const label = `${planet} in ${sign}`;
    const args = { planet, sign, house, degree };
    setInsight((cur) => (cur?.reading && cur.args?.planet === planet
      ? { ...cur, refreshing: true, includeQuestions } : { label, loading: true, args, includeQuestions }));
    try {
      const { data: r } = await api.post(`/profiles/${profileId}/explain-placement`,
        { planet, mode: "degree_aware", include_reflection_questions: includeQuestions });
      setInsight({ label: r.reading.placement_label, reading: r.reading, args, includeQuestions });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "degree_reading_unavailable") setInsight({ label, args, errorReason: d.reason });
      else {
        setInsight((cur) => (cur?.reading ? { ...cur, refreshing: false, includeQuestions: !includeQuestions } : null));
        toast.error(d?.message || "Could not fetch insight");
      }
    }
  };

  const explainStellium = async (sign, planets, element) => {
    if (!interactive) return;
    const label = `Stellium in ${sign}`;
    setInsight({ label, loading: true });
    try {
      const { data: r } = await api.post(`/profiles/${profileId}/explain-stellium`, { sign, planets, element });
      setInsight({ label, text: r.text });
    } catch { setInsight(null); toast.error("Could not fetch insight"); }
  };

  const saveMbti = async (mbti) => {
    setEditingMbti(false);
    try {
      await api.patch(`/profiles/${profileId}/mbti-type`, { mbti_type: mbti });
      onUpdate && onUpdate();
      if (synthesis && synthesis.length && onRegenerate) {
        toast.success("MBTI updated", {
          description: "Your portrait still reflects your old type.",
          action: { label: "Refresh portrait", onClick: () => onRegenerate() },
        });
      } else {
        toast.success("MBTI updated");
      }
    } catch { toast.error("Could not update MBTI"); }
  };

  const shareImage = async () => {
    if (!shareCardRef.current || sharing) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(shareCardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], "mynsight-portrait.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My MyNsight portrait" });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "mynsight-portrait.png";
        a.click();
        toast.success("Snapshot saved — share it anywhere");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not create image");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div>
      {/* Floating AI insight card */}
      <AnimatePresence>
        {insight && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            data-testid="insight-card" ref={insightRef}
            className="scroll-mt-20 rounded-2xl border border-primary/40 bg-[#0A0A0F]/70 backdrop-blur-2xl p-5 mb-6 max-h-[75vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-2"><Sparkles size={12} /> {insight.label}</p>
                {insight.loading ? <Loader2 size={16} className="animate-spin text-primary mt-1" />
                  : insight.reading ? (
                    <DegreeReading reading={insight.reading} includeQuestions={insight.includeQuestions}
                      refreshing={insight.refreshing}
                      onToggleQuestions={(v) => { const a = insight.args; explainPlacement(a.planet, a.sign, a.house, a.degree, v); }} />
                  ) : insight.errorReason ? (
                    <DegreeReadingError reason={insight.errorReason}
                      onClassic={() => { const a = insight.args; classicPlacement(a.planet, a.sign, a.house, a.degree); }} />
                  ) : <p className="text-sm text-foreground/90 leading-relaxed">{insight.text}</p>}
              </div>
              <button onClick={() => setInsight(null)} data-testid="insight-card-close" className="sticky top-0 self-start text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Snapshot hero */}
      <div className="relative mb-8" data-testid="snapshot-hero">
        <div className="absolute -top-6 left-1/3 h-32 w-64 bg-accent/10 blur-3xl rounded-full pointer-events-none" />
        <div className="flex items-center justify-between mb-3 relative">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">You at a glance</p>
          {interactive && (
            <motion.button whileTap={{ scale: 0.96 }} data-testid="share-snapshot-btn" onClick={shareImage} disabled={sharing}
              className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground hover:text-primary border border-border hover:border-primary/50 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50">
              {sharing ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />} Share card
            </motion.button>
          )}
        </div>
        <div className="flex gap-3 overflow-x-auto pb-1 relative [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
          <StatTile testid="snapshot-sun" label="Sun" glyph={SIGN_GLYPH[n.sun_sign]} value={n.sun_sign}
            onClick={interactive && n.sun_sign ? () => explainPlacement("Sun", n.sun_sign, null, n.sun_degree ? parseFloat(n.sun_degree) : null) : null} />
          <StatTile testid="snapshot-moon" label="Moon" glyph={SIGN_GLYPH[n.moon_sign]} value={n.moon_sign}
            onClick={interactive && n.moon_sign ? () => explainPlacement("Moon", n.moon_sign, null, n.moon_degree ? parseFloat(n.moon_degree) : null) : null} />
          <StatTile testid="snapshot-rising" label="Rising" glyph={SIGN_GLYPH[n.rising_sign]} value={n.rising_sign || "—"}
            onClick={interactive && n.rising_sign ? () => explainPlacement("Rising", n.rising_sign, null, n.rising_degree ? parseFloat(n.rising_degree) : null) : null} />
          <StatTile testid="snapshot-hdtype" label="HD Type" value={hd.type}
            onClick={interactive && hd.type ? () => { setTab("human-design"); } : null} />
          <StatTile testid="snapshot-lifepath" label="Life Path" value={num.life_path}
            onClick={interactive ? () => { setTab("numerology"); } : null} />
          <StatTile testid="snapshot-mbti" label="MBTI" value={psy?.mbti_type || "—"}
            onClick={interactive ? () => { setTab("psychology"); } : null} />
          <StatTile testid="snapshot-chinese" label="Chinese" value={chin.animal}
            onClick={interactive ? () => { setTab("numerology"); } : null} />
        </div>
      </div>

      {/* Sticky tab navigation */}
      <div className="sticky top-2 z-30 mb-8">
        <div className="inline-flex gap-1 p-1 rounded-full border border-white/10 bg-[#0A0A0F]/70 backdrop-blur-2xl max-w-full overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
          {TABS.map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`relative shrink-0 px-4 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === t.k ? "text-[#0A0A0F]" : "text-muted-foreground hover:text-foreground"}`}>
              {tab === t.k && (
                <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-primary" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
              )}
              <span className="relative z-10">{t.l}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div key={tab} {...tabFade}>
          {tab === "overview" && (
            <OverviewTab synthesis={synthesis} pick={pick} n={n} hd={hd} num={num} psy={psy} hasPsy={!!psy} />
          )}

          {tab === "astrology" && (
            <div data-testid="tab-panel-astrology">
              <SectionHeader overline="Natal Astrology" title="The sky at your first breath"
                sub="Your Big Three, any stelliums, and every placement — each split into its decan. Tap anything for a reading." />
              <div data-testid="panel-astrology">
                <AstrologySection natal={n} interactive={interactive} onExplainPlacement={explainPlacement} onExplainStellium={explainStellium} />
              </div>
              <AstroExplainer natal={n} />
              <InsightCallout testid="insight-callout-astrology" text={pick("hdastrology")?.text} />
            </div>
          )}

          {tab === "human-design" && (
            <div data-testid="tab-panel-human-design">
              <SectionHeader overline="Human Design" title="Your energetic mechanics"
                sub="How you're wired to make decisions and where your energy is consistent." />
              <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5 mb-6"
                onClick={interactive ? () => explainSystem("Human Design", `${hd.type}, ${hd.authority} authority, profile ${hd.profile}, strategy ${hd.strategy}`, "Human Design") : undefined}
                data-testid="hd-vitals" role={interactive ? "button" : undefined}>
                <div className="font-serif text-3xl text-primary mb-3">{hd.type || "—"}</div>
                <div className="grid sm:grid-cols-3 gap-x-8">
                  <Row label="Authority" value={hd.authority} />
                  <Row label="Profile" value={hd.profile} />
                  <Row label="Strategy" value={hd.strategy} />
                </div>
                <p className="text-xs text-muted-foreground mt-3">{(hd.defined_centers || []).length} of 9 centers defined{interactive ? " · tap for a reading" : ""}</p>
              </div>
              {profileId && <HumanDesignChart hd={hd} profileId={profileId} />}
              <InsightCallout testid="insight-callout-human-design" text={pick("integration")?.text} />
            </div>
          )}

          {tab === "psychology" && (
            <div data-testid="tab-panel-psychology">
              <SectionHeader overline="Psychology" title="How your mind is measured"
                sub="Evidence-based types and traits from your assessment." />
              {psy ? (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5" data-testid="psy-types">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">MBTI</div>
                          {editingMbti ? (
                            <select data-testid="mbti-dropdown-trigger" defaultValue={psy.mbti_type} onChange={(e) => saveMbti(e.target.value)}
                              className="mt-1 text-sm px-3 py-2 rounded-lg bg-[#0A0A0F] border border-primary/40 outline-none">
                              {MBTI_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          ) : (
                            <div className="font-serif text-3xl text-primary flex items-center gap-2">
                              {psy.mbti_type}
                              {interactive && <button data-testid="edit-mbti-btn" onClick={() => setEditingMbti(true)} className="text-muted-foreground hover:text-primary"><Pencil size={14} /></button>}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Enneagram</div>
                          <div className="font-serif text-3xl text-accent">{psy.enneagram_type ?? "—"}</div>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5" data-testid="psy-bigfive">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Big Five</div>
                      {psy.big_five && Object.entries(psy.big_five).map(([k, v]) => (
                        <div key={k} className="mb-2">
                          <div className="flex justify-between text-xs text-muted-foreground capitalize"><span>{k}</span><span>{v}</span></div>
                          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><motion.div className="h-full bg-primary" initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.7 }} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {psy.allport && (
                    <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5 mt-4" data-testid="psy-allport">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Values (Allport)</div>
                      <div className="grid sm:grid-cols-2 gap-x-6">
                        {Object.entries(psy.allport).map(([k, v]) => (
                          <div key={k} className="mb-2">
                            <div className="flex justify-between text-xs text-muted-foreground capitalize"><span>{k}</span><span>{v}</span></div>
                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><motion.div className="h-full bg-accent" initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.7 }} /></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <InsightCallout testid="insight-callout-psychology" text={pick("psychologynumerology")?.text} />
                </>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
                  <Brain size={28} className="text-primary mx-auto mb-3" />
                  <p className="font-serif text-2xl mb-2">Your psychology is still hidden</p>
                  <p className="text-sm text-muted-foreground">Take the assessment to unlock MBTI, Enneagram, Big Five and your unified synthesis.</p>
                </div>
              )}
            </div>
          )}

          {tab === "numerology" && (
            <div data-testid="tab-panel-numerology">
              <SectionHeader overline="Numerology & Chinese Zodiac" title="Your numbers & your year"
                sub="The rhythms encoded in your birth date." />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5"
                  onClick={interactive ? () => explainSystem("Numerology", `Life Path ${num.life_path}, Birthday ${num.birthday_number}, Personal Year ${num.personal_year}`, "Numerology") : undefined}
                  data-testid="numerology-card" role={interactive ? "button" : undefined}>
                  <div className="flex items-baseline gap-2 mb-3">
                    <Hash size={16} className="text-primary" />
                    <span className="font-serif text-4xl text-primary">{num.life_path ?? "—"}</span>
                    <span className="text-sm text-muted-foreground">Life Path</span>
                  </div>
                  <Row label="Birthday Number" value={num.birthday_number} />
                  <Row label="Personal Year" value={num.personal_year} />
                  {num.expression_number != null && <Row label="Expression" value={num.expression_number} />}
                  {interactive && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 mt-3">Tap for a reading</p>}
                </div>
                <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5"
                  onClick={interactive ? () => explainSystem("Chinese Zodiac", `${chin.polarity} ${chin.element} ${chin.animal}`, "Chinese Zodiac") : undefined}
                  data-testid="chinese-card" role={interactive ? "button" : undefined}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Chinese Zodiac</div>
                  <div className="font-serif text-3xl text-primary mb-3">{chin.polarity} {chin.element} {chin.animal}</div>
                  <Row label="Element" value={chin.element} />
                  <Row label="Polarity" value={chin.polarity} />
                  <Row label="Year" value={chin.year} />
                </div>
              </div>
              <InsightCallout testid="insight-callout-numerology" text={pick("psychologynumerology")?.text} />
            </div>
          )}

          {tab === "growth" && (
            <GrowthTab birthDate={data.birth_date} personalYear={num.personal_year} />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Off-screen card for image export */}
      <ShareCard ref={shareCardRef} name={data.birth_name} natal={n} hd={hd} num={num} psy={psy} chinese={chin} />
    </div>
  );
}

// ---- Overview tab: editorial reading ----
function OverviewTab({ synthesis, pick, hasPsy }) {
  const reading = ["Life Theme", "Growth Path", "Shadow Work", "Integration"]
    .map((frag) => pick(frag))
    .filter(Boolean);

  if (!hasPsy || !reading.length) {
    return (
      <div className="max-w-prose mx-auto text-center py-12" data-testid="overview-empty">
        <Sparkles size={28} className="text-primary mx-auto mb-4" />
        <h2 className="font-serif text-3xl font-light mb-3">Your portrait is almost complete</h2>
        <p className="text-muted-foreground leading-relaxed">
          Once you finish the psychology assessment, your systems are woven into one unified reading —
          a personal portrait that lives right here.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-prose mx-auto py-4" data-testid="overview-reading">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-accent/90 mb-2">Your unified portrait</p>
      <div className="space-y-10">
        {reading.map((s, i) => (
          <motion.section key={s.tag} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <h3 className="font-serif text-2xl font-light text-primary mb-3">{s.tag}</h3>
            <p className={`font-serif text-xl leading-relaxed text-foreground/90 ${i === 0 ? "first-letter:text-5xl first-letter:font-normal first-letter:text-primary first-letter:mr-1 first-letter:float-left first-letter:leading-[0.85]" : ""}`}>
              {s.text}
            </p>
          </motion.section>
        ))}
      </div>
    </div>
  );
}


// ---- Growth tab: Saturn returns + personal-year cycle ----
function GrowthTab({ birthDate, personalYear }) {
  const age = ageFromBirthDate(birthDate);
  const returns = saturnReturns(age);
  const chapter = saturnChapter(age);
  const py = Number(personalYear);
  const current = PERSONAL_YEAR[py];

  const statusStyle = {
    now: "border-primary text-primary bg-primary/10",
    past: "border-border text-muted-foreground",
    upcoming: "border-accent/40 text-accent",
    unknown: "border-border text-muted-foreground",
  };

  return (
    <div data-testid="tab-panel-growth">
      <SectionHeader overline="Growth over time" title="Your life cycles"
        sub="The long rhythms shaping your path — Saturn's ~29-year returns and your personal-year cycle." />

      {/* Saturn returns */}
      <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5 mb-4" data-testid="saturn-returns">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={15} className="text-primary" />
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Saturn Returns</p>
        </div>
        {age != null && (
          <p className="text-sm text-muted-foreground mb-4">
            You're {age} — currently in your <span className="text-foreground">{chapter?.label}</span>, {chapter?.note}.
          </p>
        )}
        <div className="space-y-3">
          {returns.map((r) => (
            <div key={r.n} data-testid={`saturn-return-${r.n}`} className="flex items-start gap-3">
              <span className={`shrink-0 mt-0.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${statusStyle[r.status]}`}>
                {r.status === "now" ? "Now" : r.status === "past" ? "Passed" : "Ahead"}
              </span>
              <div>
                <div className="font-serif text-lg">{r.label} <span className="text-muted-foreground text-sm">· age {r.range[0]}–{r.range[1]}</span></div>
                <p className="text-sm text-foreground/75 leading-relaxed">{r.theme}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Personal year cycle */}
      <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-5" data-testid="personal-year">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Personal-year cycle</p>
        {current ? (
          <div className="mb-5">
            <div className="flex items-baseline gap-3">
              <span className="font-serif text-5xl text-primary">{py}</span>
              <div>
                <div className="font-serif text-2xl">{current.title}</div>
                <div className="text-xs text-muted-foreground">Personal Year {py} of 9</div>
              </div>
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed mt-3">{current.theme}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mb-4">Your personal year isn't available.</p>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-9 gap-1.5">
          {Object.entries(PERSONAL_YEAR).map(([num, info]) => {
            const isNow = Number(num) === py;
            return (
              <div key={num} title={`${num} · ${info.title}`} data-testid={`py-${num}`}
                className={`rounded-lg border py-2 text-center ${isNow ? "border-primary bg-primary/10" : "border-border/60"}`}>
                <div className={`font-serif text-lg ${isNow ? "text-primary" : "text-muted-foreground"}`}>{num}</div>
                <div className="text-[9px] text-muted-foreground leading-tight px-0.5">{info.title}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">A 9-year arc from fresh starts (1) to completion (9), then it renews.</p>
      </div>
    </div>
  );
}

// ---- Plain-language explanation of stelliums & decans ----
function AstroExplainer({ natal }) {
  const stelliums = stelliumsOf(natal);
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="astro-explainer">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="explain-decans">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary mb-2">What's a decan?</p>
        <p className="text-sm text-foreground/85 leading-relaxed">
          Every zodiac sign spans 30°, divided into three 10° slices called <span className="text-foreground">decans</span>.
          Each decan is sub-ruled by another sign of the same element, adding a second, subtler flavour on top of the main
          sign. So two people with the same Sun sign can feel quite different depending on their decan.
        </p>
        <p className="text-xs text-muted-foreground mt-2.5">
          The tags above (e.g. “2nd decan · Sagittarius”) show each placement's decan and its sub-ruler.
        </p>
      </div>
      <div className="rounded-2xl border border-accent/25 bg-accent/[0.05] p-5" data-testid="explain-stelliums">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent/90 mb-2">What's a stellium?</p>
        <p className="text-sm text-foreground/85 leading-relaxed">
          When <span className="text-foreground">three or more planets</span> gather in a single sign, that's a{" "}
          <span className="text-accent">stellium</span> — an intense concentration of energy that makes that sign's themes
          a dominant, recurring force in your life.
        </p>
        {stelliums.length > 0 ? (
          <p className="text-xs text-foreground/80 mt-2.5">
            Yours is in <span className="text-accent">{stelliums[0].sign}</span> ({stelliums[0].planets.length} planets),
            so its {stelliums[0].element?.toLowerCase()} themes are strongly amplified for you.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-2.5">
            You don't have a stellium — your planetary energy is spread more evenly across the wheel.
          </p>
        )}
      </div>
    </div>
  );
}
